import { SignupData } from '../components/SignupForm/SignupForm';
import { LoginUserData } from '../components/LoginForm/LoginForm';

// Mock database to store organizations, users, teams, and sprints
interface MockDatabase {
  organizations: Array<{
    id: string;
    name: string;
    industry: string;
    size: string;
    website?: string;
    createdAt: Date;
    plan: string;
  }>;
  users: Array<{
    id: string;
    organizationId: string;
    firstName: string;
    lastName: string;
    email: string;
    jobTitle: string;
    role: 'ADMIN' | 'MEMBER';
    createdAt: Date;
    password: string; // In real app, this would be hashed
    teamIds: string[]; // Teams this user belongs to
     emailVerified: boolean;
  }>;
  teams: Array<{
    id: string;
    organizationId: string;
    name: string;
    description: string;
    memberIds: string[];
    adminIds: string[];
    createdAt: Date;
    createdBy: string;
  }>;
  sprints: Array<{
    id: string;
    organizationId: string;
    teamId: string;
    name: string;
    description: string;
    startDate: Date;
    endDate: Date;
    status: 'planning' | 'active' | 'completed';
    createdAt: Date;
    createdBy: string;
  }>;
}

// Mock database instance
const mockDatabase: MockDatabase = {
  organizations: [],
  users: [],
  teams: [],
  sprints: []
};



// Mock API functions
export const mockAPI = {
  // Create organization and admin user
  createOrganization: async (signupData: SignupData): Promise<{ success: boolean; organizationId: string; userId: string }> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Generate IDs
    const organizationId = `org_${Date.now()}`;
    const userId = `user_${Date.now()}`;

    // Check if email already exists
    const existingUser = mockDatabase.users.find(user => user.email === signupData.user.email);
    if (existingUser) {
      throw new Error('Email already exists');
    }

    // Create organization
    const organization = {
      id: organizationId,
      name: signupData.organization.name,
      industry: signupData.organization.industry,
      size: signupData.organization.size,
      website: signupData.organization.website,
      createdAt: new Date(),
      plan: signupData.plan
    };

    // Create admin user
    const user = {
      id: userId,
      organizationId,
      firstName: signupData.user.firstName,
      lastName: signupData.user.lastName,
      email: signupData.user.email,
      jobTitle: signupData.user.jobTitle,
      role: signupData.user.role,
      createdAt: new Date(),
      password: signupData.user.password, // In real app, this would be hashed
      teamIds: [], // Initialize with empty teams
      emailVerified: false // New users should start unverified
    };

    // Save to mock database
    mockDatabase.organizations.push(organization);
    mockDatabase.users.push(user);

    // Log the data (for development)
    console.log('Organization created:', organization);
    console.log('Admin user created:', user);
    console.log('Current database state:', mockDatabase);

    return {
      success: true,
      organizationId,
      userId
    };
  },

  // Login user
  loginUser: async (email: string, password: string): Promise<LoginUserData> => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Find user by email
    const user = mockDatabase.users.find(u => u.email === email);
    if (!user) {
      throw new Error('Invalid email or password');
    }

    // Check password (in real app, compare with hashed password)
    if (user.password !== password) {
      throw new Error('Invalid email or password');
    }

    // Get organization details
    const organization = mockDatabase.organizations.find(org => org.id === user.organizationId);
    if (!organization) {
      throw new Error('Organization not found');
    }

    // Return user data with organization info
    const loginData: LoginUserData = {
      id: user.id,
      organizationId: user.organizationId,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      jobTitle: user.jobTitle,
      role: user.role,
      teamIds: user.teamIds, // Include teamIds
      emailVerified: user.emailVerified || false, // Include email verification status
      emailNotifications: true, // Default to true for mock data
      organization: {
        id: organization.id,
        name: organization.name,
        industry: organization.industry,
        size: organization.size,
        plan: organization.plan
      }
    };

    console.log('User logged in successfully:', loginData);
    console.log('User name:', loginData.firstName, loginData.lastName);
    return loginData;
  },

  // Team Management Functions
  createTeam: async (organizationId: string, teamData: {
    name: string;
    description: string;
    createdBy: string;
  }) => {
    await new Promise(resolve => setTimeout(resolve, 500));

    const teamId = `team_${Date.now()}`;
    const team = {
      id: teamId,
      organizationId,
      name: teamData.name,
      description: teamData.description,
      memberIds: [],
      adminIds: [teamData.createdBy], // Creator is admin by default
      createdAt: new Date(),
      createdBy: teamData.createdBy
    };

    mockDatabase.teams.push(team);
    console.log('Team created:', team);
    return team;
  },

  getTeamsByOrganization: async (organizationId: string) => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return mockDatabase.teams.filter(team => team.organizationId === organizationId);
  },

  addUserToTeam: async (teamId: string, userId: string) => {
    await new Promise(resolve => setTimeout(resolve, 300));

    const team = mockDatabase.teams.find(t => t.id === teamId);
    const user = mockDatabase.users.find(u => u.id === userId);

    if (!team || !user) {
      throw new Error('Team or user not found');
    }

    // Add user to team if not already member
    if (!team.memberIds.includes(userId)) {
      team.memberIds.push(userId);
    }

    // Add team to user's teams if not already there
    if (!user.teamIds.includes(teamId)) {
      user.teamIds.push(teamId);
    }

    console.log(`User ${userId} added to team ${teamId}`);
    return { team, user };
  },

  removeUserFromTeam: async (teamId: string, userId: string) => {
    await new Promise(resolve => setTimeout(resolve, 300));

    const team = mockDatabase.teams.find(t => t.id === teamId);
    const user = mockDatabase.users.find(u => u.id === userId);

    if (!team || !user) {
      throw new Error('Team or user not found');
    }

    // Remove user from team
    team.memberIds = team.memberIds.filter(id => id !== userId);
    team.adminIds = team.adminIds.filter(id => id !== userId);

    // Remove team from user's teams
    user.teamIds = user.teamIds.filter(id => id !== teamId);

    console.log(`User ${userId} removed from team ${teamId}`);
    return { team, user };
  },

  // Sprint Management Functions
  createSprint: async (sprintData: {
    organizationId: string;
    teamId: string;
    name: string;
    description: string;
    startDate: Date;
    endDate: Date;
    createdBy: string;
  }) => {
    await new Promise(resolve => setTimeout(resolve, 500));

    // Validate dates
    if (sprintData.startDate >= sprintData.endDate) {
      throw new Error('Start date must be before end date');
    }

    if (sprintData.startDate < new Date()) {
      throw new Error('Start date cannot be in the past');
    }

    const sprintId = `sprint_${Date.now()}`;
    const sprint = {
      id: sprintId,
      organizationId: sprintData.organizationId,
      teamId: sprintData.teamId,
      name: sprintData.name,
      description: sprintData.description,
      startDate: sprintData.startDate,
      endDate: sprintData.endDate,
      status: 'planning' as const,
      createdAt: new Date(),
      createdBy: sprintData.createdBy
    };

    mockDatabase.sprints.push(sprint);
    console.log('Sprint created:', sprint);
    return sprint;
  },

  getSprintsByTeam: async (teamId: string) => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return mockDatabase.sprints.filter(sprint => sprint.teamId === teamId);
  },

  getSprintsByOrganization: async (organizationId: string) => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return mockDatabase.sprints.filter(sprint => sprint.organizationId === organizationId);
  },

  updateSprintStatus: async (sprintId: string, status: 'planning' | 'active' | 'completed') => {
    await new Promise(resolve => setTimeout(resolve, 300));

    const sprint = mockDatabase.sprints.find(s => s.id === sprintId);
    if (!sprint) {
      throw new Error('Sprint not found');
    }

    sprint.status = status;
    console.log(`Sprint ${sprintId} status updated to ${status}`);
    return sprint;
  },

  // User Management Functions
  getUsersByOrganization: async (organizationId: string) => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return mockDatabase.users.filter(user => user.organizationId === organizationId);
  },

  createUser: async (userData: {
    organizationId: string;
    firstName: string;
    lastName: string;
    email: string;
    jobTitle: string;
    role: 'ADMIN' | 'MEMBER';
    password: string;
    createdBy: string;
  }) => {
    await new Promise(resolve => setTimeout(resolve, 500));

    // Check if email already exists
    const existingUser = mockDatabase.users.find(user => user.email === userData.email);
    if (existingUser) {
      throw new Error('Email already exists');
    }

    const userId = `user_${Date.now()}`;
    const user = {
      id: userId,
      organizationId: userData.organizationId,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      jobTitle: userData.jobTitle,
      role: userData.role,
      createdAt: new Date(),
      password: userData.password,
      teamIds: [],
      emailVerified: false // New users should start unverified
    };

    mockDatabase.users.push(user);
    console.log('User created:', user);
    return user;
  },

  // Get organization details
  getOrganization: async (organizationId: string) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const organization = mockDatabase.organizations.find(org => org.id === organizationId);
    if (!organization) {
      throw new Error('Organization not found');
    }

    const users = mockDatabase.users.filter(user => user.organizationId === organizationId);
    
    return {
      organization,
      users
    };
  },

  // Get user details
  getUser: async (userId: string) => {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const user = mockDatabase.users.find(u => u.id === userId);
    if (!user) {
      throw new Error('User not found');
    }

    return user;
  },

  // Get all organizations (for admin purposes)
  getAllOrganizations: () => {
    return mockDatabase.organizations;
  },

  // Get all users (for admin purposes)
  getAllUsers: () => {
    return mockDatabase.users;
  },

  // Clear database (for testing)
  clearDatabase: () => {
    mockDatabase.organizations.length = 0;
    mockDatabase.users.length = 0;
  }
};
